import React, { useCallback, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import Layout from '../../../../components/Layout';
import { MainColumn } from '../../../../components/Layout/ThirdPaneLayout';
import { useQuery } from '@apollo/client';
import useNotify from '../../../../hooks/useNotify';
// import PropTypes from 'prop-types'
import Accordion from '../../../../components/Accordion/Accordion';
import AccordionItem from '../../../../components/Accordion/AccordionItem';
import DataTable from '../../../../components/Table/DataTable';
import { PortalWithState } from 'react-portal';
import { Flex, Text } from 'rebass';
import GaugeEditModal from '../../../../components/Gauge/GaugeEditModal';
import CredentialEditModal from '../../../../components/Credential/CredentialEditModal';
import gaugesColumns from '../../../../components/Tester/Gauges.columns';
import credentialsColumns from '../../../../components/Tester/Credentials.columns';
import {
  Address as AddressType,
  Credential,
  Gauge,
} from '../../../../components/Tester/types';
import { GET_MY_TESTER_PROFILE } from './TesterProfile.query';
import SelfEditModal from '../../../../components/User/SelfEditModal';
import PageBar from '../../../../components/PageBar/PageBar';
import Button from '../../../../components/Button/Button';
import {
  EmailInput,
  PhoneInput,
  ProfileType,
} from '../../../../components/Profile/types';
import Perm from '../../../../types/perm';
import Address from '../../../../components/Address/Address';
import { ApolloError } from '@apollo/client/errors';

type TesterProfileViewParams = Record<string, never>;

type TesterProfileViewProps = RouteComponentProps<TesterProfileViewParams>;

type Me = {
  id: string;
  type: ProfileType;
  name: string;
  firstName: string;
  lastName: string;
  contactAddressId: string | null;
  contactAddress: AddressType | null;
  phones: PhoneInput[];
  emails: EmailInput[];

  backflowTester: {
    gauges: Gauge[];
  };
  credentials: Credential[];

  perms: Perm[];

  user: {
    profileId: string;
    email: string;
  };
};

const TesterProfileView: React.FC<TesterProfileViewProps> = (props) => {
  const {
    loading: itemLoading,
    error: itemError,
    data: itemData,
    refetch,
  } = useQuery<{
    me: Me;
  }>(GET_MY_TESTER_PROFILE, {
    // skip: !vendorId,
    // variables: { where: { id: vendorId || null } },
    onError: useCallback((error: ApolloError) => {
      showError(`Error loading tester profile`, error);
    }, []),
    fetchPolicy: 'network-only',
  });

  // TODO: handle error state
  // TODO: handle loading state

  const { showError } = useNotify();

  const profile = itemData?.me;
  const testerUser = profile?.user;

  // State
  const [selectedCredential, setSelectedCredential] = useState<Credential>();
  const [selectedGauge, setSelectedGauge] = useState<Gauge>();

  // Safety Cehck
  // How can we avoid this?
  if (!profile || !testerUser) {
    return (
      <Layout>
        <MainColumn px="mainContent" pb="mainContent" height="inherit">
          <PageBar title="Profile" />
          Loading...
        </MainColumn>
      </Layout>
    );
  }

  // Conditions
  const hasCredentials = !!profile?.credentials?.length;
  const hasGauges = !!profile?.backflowTester?.gauges?.length;

  // Destruct
  const {
    credentials,
    backflowTester: { gauges },
  } = profile;

  return (
    <Layout>
      <MainColumn px="mainContent" pb="mainContent" height="inherit">
        {/* This should get hoisted */}
        <PageBar title="Profile" />
        <Flex flexDirection="column" maxWidth={600}>
          {/* Contact */}
          <Accordion
            title="Contact"
            variant="onMain"
            icon={
              <PortalWithState closeOnEsc>
                {({ openPortal, closePortal, portal }) => (
                  <>
                    <Button
                      variant="secondary"
                      onClick={openPortal}
                      icon="edit"
                    >
                      Edit
                    </Button>
                    {portal(
                      <SelfEditModal
                        onClose={closePortal}
                        profile={profile}
                        hide={['type', 'billingAddressId']}
                      />
                    )}
                  </>
                )}
              </PortalWithState>
            }
          >
            <AccordionItem label="Name" value={profile.name} />
            {profile.phones.map((phone, idx) => (
              <AccordionItem
                key={phone.id}
                label={
                  phone.subType === 'UNKNOWN'
                    ? `Phone ${idx + 1}`
                    : `Phone (${phone.subType})`
                }
                value={phone.value}
              />
            ))}
            {profile.emails.map((email, idx) => (
              <AccordionItem key={email.id} label="Email" value={email.value} />
            ))}
            {profile.contactAddress && (
              <AccordionItem
                label="Mailing Address"
                value={<Address address={profile.contactAddress} />}
              />
            )}
          </Accordion>
          {/* Creds */}
          <Accordion
            title="Credentials"
            variant="onMain"
            icon={
              <PortalWithState closeOnEsc>
                {({ openPortal, closePortal, portal }) => (
                  <>
                    <Button variant="primary" icon="plus" onClick={openPortal}>
                      Credential
                    </Button>
                    {portal(
                      <CredentialEditModal
                        action="create"
                        credential={{
                          profileId: profile.id,
                          type: 'BACKFLOW',
                        }}
                        onClose={() => {
                          refetch();
                          closePortal();
                        }}
                      />
                    )}
                  </>
                )}
              </PortalWithState>
            }
          >
            {hasCredentials && (
              <PortalWithState closeOnEsc>
                {({ openPortal, closePortal, portal }) => (
                  <>
                    <DataTable
                      columns={credentialsColumns}
                      items={credentials}
                      onRowClick={(credential) => {
                        setSelectedCredential(credential);
                        openPortal();
                      }}
                    />
                    {portal(
                      <>
                        {selectedCredential && (
                          <CredentialEditModal
                            action="edit"
                            credential={{
                              ...selectedCredential,
                              profileId: profile.id,
                            }}
                            onClose={() => {
                              refetch();
                              closePortal();
                            }}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </PortalWithState>
            )}
            {!hasCredentials && (
              <Text variant="body2" color="fieldBorderHover">
                There are no credentials yet.
              </Text>
            )}
          </Accordion>

          {/* Gauges */}
          <Accordion
            variant="onMain"
            title="Gauges"
            icon={
              <PortalWithState closeOnEsc>
                {({ openPortal, closePortal, portal }) => (
                  <>
                    <Button variant="primary" icon="plus" onClick={openPortal}>
                      Gauge
                    </Button>
                    {portal(
                      <GaugeEditModal
                        action="create"
                        gauge={{
                          profileId: profile.id,
                        }}
                        onClose={() => {
                          refetch();
                          closePortal();
                        }}
                      />
                    )}
                  </>
                )}
              </PortalWithState>
            }
          >
            {hasGauges && (
              <PortalWithState closeOnEsc>
                {({ openPortal, closePortal, portal }) => (
                  <>
                    <DataTable
                      columns={gaugesColumns}
                      items={gauges}
                      onRowClick={(gauge) => {
                        setSelectedGauge(gauge);
                        openPortal();
                      }}
                    />
                    {portal(
                      <>
                        {selectedGauge && (
                          <GaugeEditModal
                            action="edit"
                            gauge={{
                              ...selectedGauge,
                              profileId: profile.id,
                            }}
                            onClose={() => {
                              refetch();
                              closePortal();
                            }}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </PortalWithState>
            )}
            {!hasGauges && (
              <Text variant="body2" color="fieldBorderHover">
                There are no gauges yet.
              </Text>
            )}
          </Accordion>
        </Flex>
      </MainColumn>
    </Layout>
  );
};

export default TesterProfileView;
